import React from 'react'
import { Instagram, MusicNote, OpenInNew, YouTube } from '@mui/icons-material'
import { Avatar, Box, Button, Chip, Container, Grid, Paper, Stack, Typography } from '@mui/material'

type Partner = {
  name: string
  handle?: string
  description?: string
  avatarSrc?: string
  socials: {
    youtube?: string
    instagram?: string
    tiktok?: string
  }
}

const partners: Partner[] = [
  {
    name: 'JeheFenix',
    handle: '@jehefenix',
    description: 'Criador de conteúdo e parceiro da comunidade.',
    avatarSrc: '/partners/jeh_e_fenix.jpg',
    socials: {
      youtube: 'https://youtube.com/@jehefenix?si=znMJXh41j64_eseX',
      instagram: 'https://www.instagram.com/jehefenix?igsh=Nmx3b2dtYWh3M282',
      tiktok: 'https://www.tiktok.com/@jehefenix?_r=1&_t=ZS-93jAzJ8GyoR'
    }
  }
]

const SocialButton: React.FC<{
  label: string
  href?: string
  startIcon: React.ReactNode
  sx?: object
}> = ({ label, href, startIcon, sx }) => {
  if (!href) return null

  return (
    <Button
      fullWidth
      variant="contained"
      startIcon={startIcon}
      endIcon={<OpenInNew sx={{ fontSize: 18 }} />}
      component="a"
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      sx={{
        py: 1.2,
        fontWeight: 900,
        borderRadius: 2,
        ...sx
      }}
    >
      {label}
    </Button>
  )
}

const PartnershipsSection: React.FC = () => {
  return (
    <Box
      id="parcerias"
      sx={{
        py: { xs: 7, md: 9 },
        bgcolor: 'background.default',
        backgroundImage:
          'radial-gradient(900px 420px at 20% 0%, rgba(255, 109, 0, 0.12) 0%, transparent 60%), radial-gradient(900px 420px at 80% 20%, rgba(0, 230, 118, 0.10) 0%, transparent 60%)'
      }}
    >
      <Container maxWidth="lg">
        <Box textAlign="center" mb={{ xs: 4, md: 6 }}>
          <Typography
            variant="h3"
            component="h2"
            sx={{
              fontWeight: 800,
              background: 'linear-gradient(45deg, #ff6d00 20%, #00e676 80%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
              mb: 1
            }}
          >
            Parcerias
          </Typography>
          <Typography variant="h6" color="text.secondary" sx={{ maxWidth: 820, mx: 'auto' }}>
            Criadores e projetos que caminham com a gente. Confira e siga nossos parceiros nas redes.
          </Typography>
        </Box>

        <Grid container spacing={3} justifyContent="center">
          {partners.map((partner) => (
            <Grid item xs={12} md={10} lg={8} key={partner.name}>
              <Paper
                elevation={0}
                sx={{
                  borderRadius: 4,
                  backgroundColor: 'rgba(26, 26, 26, 0.72)',
                  border: '1px solid rgba(255, 255, 255, 0.10)',
                  backdropFilter: 'blur(10px)',
                  overflow: 'hidden',
                  position: 'relative',
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    inset: 0,
                    background:
                      'linear-gradient(135deg, rgba(255, 109, 0, 0.18) 0%, rgba(0, 230, 118, 0.10) 45%, rgba(33, 150, 243, 0.10) 100%)',
                    opacity: 0.45,
                    pointerEvents: 'none'
                  },
                  '&:hover': {
                    border: '1px solid rgba(255, 255, 255, 0.18)',
                    boxShadow:
                      '0 26px 60px rgba(0, 0, 0, 0.55), 0 10px 24px rgba(255, 109, 0, 0.10)',
                    transform: 'translateY(-3px)'
                  },
                  transition: 'all 0.25s ease'
                }}
              >
                <Box sx={{ position: 'relative', zIndex: 1 }}>
                  <Box
                    sx={{
                      px: { xs: 3, md: 4 },
                      py: { xs: 3, md: 3.5 },
                      borderBottom: '1px solid rgba(255, 255, 255, 0.08)',
                      background:
                        'linear-gradient(135deg, rgba(0, 0, 0, 0.40) 0%, rgba(0, 0, 0, 0.12) 100%)'
                    }}
                  >
                    <Stack
                      direction={{ xs: 'column', sm: 'row' }}
                      spacing={{ xs: 2, sm: 3 }}
                      alignItems="center"
                    >
                      <Avatar
                        alt={partner.name}
                        src={partner.avatarSrc}
                        sx={{
                          width: 92,
                          height: 92,
                          fontSize: 34,
                          fontWeight: 900,
                          bgcolor: 'secondary.main',
                          color: 'secondary.contrastText',
                          border: '4px solid rgba(255, 255, 255, 0.12)',
                          boxShadow: '0 14px 28px rgba(0, 0, 0, 0.45)',
                          '& img': { objectFit: 'cover' }
                        }}
                      >
                        {partner.name.slice(0, 1).toUpperCase()}
                      </Avatar>

                      <Box sx={{ flex: 1, textAlign: { xs: 'center', sm: 'left' } }}>
                        <Stack
                          direction={{ xs: 'column', sm: 'row' }}
                          spacing={1.5}
                          alignItems={{ xs: 'center', sm: 'center' }}
                          justifyContent="space-between"
                        >
                          <Box>
                            <Typography variant="h4" fontWeight={900} color="text.primary">
                              {partner.name}
                            </Typography>
                            {partner.handle && (
                              <Typography variant="body1" color="text.secondary" sx={{ mt: 0.25 }}>
                                {partner.handle}
                              </Typography>
                            )}
                          </Box>

                          <Chip
                            label="Parceiro"
                            color="secondary"
                            sx={{
                              fontWeight: 900,
                              borderRadius: 999,
                              px: 0.75,
                              bgcolor: 'rgba(255, 109, 0, 0.18)',
                              border: '1px solid rgba(255, 109, 0, 0.35)'
                            }}
                          />
                        </Stack>

                        {partner.description && (
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                            {partner.description}
                          </Typography>
                        )}
                      </Box>
                    </Stack>
                  </Box>

                  <Box sx={{ px: { xs: 3, md: 4 }, py: { xs: 3, md: 3.5 } }}>
                    <Typography variant="subtitle1" fontWeight={900} color="text.primary" sx={{ mb: 2 }}>
                      Siga nas redes
                    </Typography>

                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={4}>
                        <SocialButton
                          label="YouTube"
                          href={partner.socials.youtube}
                          startIcon={<YouTube />}
                          sx={{ bgcolor: '#FF0000', '&:hover': { bgcolor: '#CC0000' } }}
                        />
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <SocialButton
                          label="Instagram"
                          href={partner.socials.instagram}
                          startIcon={<Instagram />}
                          sx={{
                            background:
                              'linear-gradient(45deg, #F58529 0%, #DD2A7B 45%, #8134AF 70%, #515BD4 100%)',
                            '&:hover': {
                              background:
                                'linear-gradient(45deg, #E6771D 0%, #C81F6C 45%, #6D2C98 70%, #4250C8 100%)'
                            }
                          }}
                        />
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <SocialButton
                          label="TikTok"
                          href={partner.socials.tiktok}
                          startIcon={<MusicNote />}
                          sx={{
                            bgcolor: '#000000',
                            color: '#ffffff',
                            border: '1px solid rgba(255, 255, 255, 0.14)',
                            boxShadow: '0 10px 24px rgba(0, 0, 0, 0.35)',
                            '& .MuiButton-startIcon, & .MuiButton-endIcon': { color: 'inherit' },
                            '&:hover': { bgcolor: '#111111' }
                          }}
                        />
                      </Grid>
                    </Grid>

                    <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                      Abre em uma nova aba.
                    </Typography>
                  </Box>
                </Box>
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  )
}

export default PartnershipsSection

